/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import {
    Box,
    Link,
    useMultiStyleConfig,
    Heading
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Component, regionPropType} from '@salesforce/commerce-sdk-react/components'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site.js'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import shopBySeries from '../../../theme/components/project/shopBySeries'
import {BlackArrow} from '../../../components/icons'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getPDPath} from '../../../utils/custom-utils.js'

/**
 * Shop By series component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {string} - props.title - Text Below Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.altText - The image alt text shown by the component.
 * @returns {React.ReactElement} - ImageWithText component.
 */
export const ShopBySeriesLayout = ({
    heading,
    headingTextStyle,
    headingTextfontWeight,
    headingTextColor,
    headingTextAlignment,
    shopBySeriesType,
    regions,
    viewAllSeries,
    viewAllSeriesLink,
    target
}) => {
    const segment = useSegment()
    const {site, locale} = useMultiSite()
    const components = regions[0]?.components || []
    const styles = useMultiStyleConfig('ShopBySeriesLayoutStyles')
    const targetLink = target === 'New-Window' ? '_blank' : '_self'
    const SampleNextArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                role="button"
                aria-label="Next Slide"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
                tabindex="0"
            >
                <BlackArrow />
            </Box>
        )
    }

    const SamplePrevArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                role="button"
                aria-label="Previous Slide"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
                tabindex="0"
            >
                <BlackArrow />
            </Box>
        )
    }

    const settings = {
        infinite: true,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 4,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 640,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    }

    return (
        <Box sx={shopBySeries}>
            <Box
                className="shop-by-series"
                {...styles.shopBySeries}
                backgroundImage={`url(${getAssetUrl('static/img/bg_shop_by_series.png')})`}
            >
                <Box {...styles.shopBySeriesInner}>
                    {heading && heading !== 'undefined' && (
                        <Heading
                            as={'h2'}
                            className={`heading ${headingTextAlignment || ''}`}
                            {...styles.heading}
                            color={headingTextColor?.value || 'inherit'}
                            fontStyle={headingTextStyle || 'normal'}
                            fontWeight={
                                headingTextfontWeight === 'Bold' ? 800 : headingTextfontWeight
                            }
                            textAlign={headingTextAlignment || 'left'}
                            fontSize={{base: '2xl', md: '40px'}}
                            lineHeight={{base: '8', md: '52px'}}
                        >
                            {heading}
                        </Heading>
                    )}

                    {shopBySeriesType === 'Grid-View' ? (
                        <Box
                            className={`${shopBySeriesType} shopbyseries-grid-slider`}
                            {...styles.gridView}
                        >
                            {components.map((component, index) => (
                                <Box
                                    key={index}
                                    className="shopBySeries-component"
                                    {...styles.sbsComponent}
                                >
                                    <Component component={component} />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box className={`${shopBySeriesType} shopbyseries-grid-slider`}>
                            <Box
                                width="100%"
                                height="100%"
                                overflow={'hidden'}
                                className="slider-container"
                            >
                                <Slider {...settings}>
                                    {components.map((component, index) => (
                                        <Box key={index} data-testid="carousel">
                                            <Component component={component} />
                                        </Box>
                                    ))}
                                </Slider>
                            </Box>
                        </Box>
                    )}

                    {viewAllSeries && viewAllSeries !== 'undefined' && (
                        <Box
                            {...styles.viewAll}
                            onClick={(event) => {
                                // segment
                                let positionInContainer = 0
                                let panelTitle = ''
                                const parent = event.currentTarget?.parentElement
                                if (parent) {
                                    const viewAllLink = parent.querySelector('.view-all-series')
                                    const sliderContainer = parent.querySelector(
                                        '.shopbyseries-grid-slider'
                                    )
                                    if (viewAllLink && sliderContainer) {
                                        panelTitle = viewAllLink.textContent?.trim() || ''
                                        positionInContainer =
                                            sliderContainer.querySelectorAll(':scope > div')
                                                ?.length || 0
                                    }
                                }
                                segment.sendPanelSelectedEvent(
                                    event,
                                    {},
                                    {panelTitle, positionInContainer}
                                )
                            }}
                        >
                            <Link
                                href={getPDPath(viewAllSeriesLink, site, locale)}
                                {...styles.viewAllSeries}
                                className={`view-all-series ${
                                    viewAllSeriesLink !== 'undefined' ? '' : 'no-event-link'
                                }`}
                                backgroundImage={`url(${getAssetUrl(
                                    'static/img/right-arrow-icon.png'
                                )})`}
                                target={targetLink}
                            >
                                {viewAllSeries}
                            </Link>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

ShopBySeriesLayout.propTypes = {
    regions: PropTypes.arrayOf(regionPropType).isRequired,
    heading: PropTypes.string,
    headingTextStyle: PropTypes.string,
    headingTextfontWeight: PropTypes.string,
    headingTextColor: PropTypes.object,
    headingTextAlignment: PropTypes.string,
    viewAllSeriesLink: PropTypes.string,
    viewAllSeries: PropTypes.string,
    shopBySeriesType: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
    target: PropTypes.string
}

export default ShopBySeriesLayout
